/**
 * Device capability registry panel (DEVICE_CAPABILITY_REGISTRY_ENABLED).
 * ZD-FLEET-ENTRY: Soft index at #/devices?view=capabilities — NOT the All-devices
 * list (#/group/_all). Per-device register drawer stays on #/devices/:id.
 */

import {
  PLATFORM_CONTROL_TIMEOUT_MS,
  buildGetDevicesCapabilitiesPath,
  buildPostDeviceCapabilitiesPath,
} from "./platform-control-console-api.js";

/**
 * @param {{ canEdit?: boolean }} [opts]
 */
export function deviceCapabilitiesPanelHtml(opts = {}) {
  const canEdit = !!opts.canEdit;
  return `
    <details class="card device-drawer" id="deviceCapsPanel">
      <summary class="device-drawer__summary">
        <span class="device-drawer__title">Board features</span>
        <span class="device-drawer__hint muted">Board capabilities · expand</span>
      </summary>
      <div class="device-drawer__body">
        <p class="muted" style="margin:0 0 10px">See what this board can do, and register features when you commission it.</p>
        <div id="deviceCapsStatus" class="muted" style="margin-bottom:8px">Loading…</div>
        ${canEdit ? `
        <div class="inline-form" style="margin-top:4px">
          <label class="field"><span>Board type</span>
            <select id="deviceCapsType">
              <option value="camera_generic">Generic camera</option>
              <option value="camera_hik">Hikvision camera</option>
              <option value="camera_dahua">Dahua camera</option>
              <option value="croc_sentinel">Croc Sentinel edge</option>
              <option value="human_responder">Human responder</option>
            </select>
          </label>
          <label class="field grow"><span>Claimed features</span>
            <input id="deviceCapsClaimed" placeholder="snapshot, video" />
          </label>
          <label class="field grow"><span>Tested features (optional)</span>
            <input id="deviceCapsTested" placeholder="snapshot" />
          </label>
          <div class="row wide" style="justify-content:flex-end">
            <button class="btn btn-tap" type="button" id="deviceCapsRegister">Register</button>
          </div>
        </div>` : `<p class="muted">Admin role required to register.</p>`}
        <div id="deviceCapsOut" class="device-caps-out" style="margin-top:10px"></div>
      </div>
    </details>`;
}

/**
 * Soft capability fleet HTML for #/devices?view=capabilities (ZD-FLEET-ENTRY).
 * @param {{ catalogHtml: string, itemsHtml: string, filter: string }} opts
 */
export function deviceCapabilitiesFleetHtml(opts) {
  const escapeHtml = opts.escapeHtml || ((v) => String(v == null ? "" : v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;"));
  return `<section class="card glass-panel glass-panel--panel" aria-label="Board features">
    <p class="muted" style="margin:0 0 8px">Platform</p>
    <h2>Board features</h2>
    <p class="muted">Index of registered features. Open a board to register more. Known feature list is below.</p>
    <div class="row" style="gap:8px;flex-wrap:wrap;margin:10px 0">
      <a class="btn secondary btn-tap" href="#/group/_all">All boards</a>
      <button class="btn secondary btn-tap" type="button" id="capsFleetRefresh">Refresh</button>
    </div>
    <label class="field" style="max-width:280px"><span>Filter by feature</span>
      <input id="capsFleetFilter" value="${escapeHtml(opts.filter || "")}" placeholder="snapshot" />
    </label>
    <h3 style="margin-top:14px">Known features</h3>
    <p style="font-size:13px">${opts.catalogHtml || "—"}</p>
    <h3 style="margin-top:14px">Indexed boards</h3>
    <table class="table"><thead><tr><th>Board</th><th>Type</th><th>Capabilities</th><th></th></tr></thead>
      <tbody>${opts.itemsHtml || `<tr><td colspan="4" class="muted">No indexed boards.</td></tr>`}</tbody>
    </table>
    <p id="capsFleetMsg" class="muted" aria-live="polite"></p>
  </section>`;
}

/**
 * @param {HTMLElement} view
 * @param {{
 *   deviceId: string,
 *   routeSeq: number,
 *   canEdit: boolean,
 *   isRouteCurrent: (n: number) => boolean,
 *   api: Function,
 *   toast: Function,
 *   escapeHtml: Function,
 * }} ctx
 */
export function wireDeviceCapabilitiesPanel(view, ctx) {
  const {
    deviceId,
    routeSeq,
    canEdit,
    isRouteCurrent,
    api,
    toast,
  } = ctx;
  const status = view.querySelector("#deviceCapsStatus");
  const out = view.querySelector("#deviceCapsOut");

  const refresh = async () => {
    if (status) status.textContent = "Loading…";
    try {
      const pack = await api(buildGetDevicesCapabilitiesPath({ verified_only: "false" }), {
        timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
      });
      if (!isRouteCurrent(routeSeq)) return;
      const items = Array.isArray(pack.items) ? pack.items : [];
      const mine = items.filter((it) => String(it.device_id || it.id || "") === String(deviceId));
      const catalog = Array.isArray(pack.catalog) ? pack.catalog : [];
      if (status) {
        status.textContent = mine.length
          ? `${mine.length} record(s) for this board · ${catalog.length} known features.`
          : `No registry row for this board yet · ${catalog.length} known features.`;
      }
      if (out) {
        const esc = (v) => String(v == null ? "" : v)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
        const claimed = mine
          .flatMap((it) => (Array.isArray(it.claimed) ? it.claimed : Array.isArray(it.capabilities) ? it.capabilities : []))
          .map((x) => String(x));
        const rows = claimed.length
          ? claimed.map((c) => `<tr><td>${esc(c)}</td><td>Registered</td></tr>`).join("")
          : `<tr><td colspan="2" class="muted">No features registered yet.</td></tr>`;
        const cat = catalog.length
          ? catalog.map((c) => esc(typeof c === "string" ? c : c.slug || c.id || "")).filter(Boolean).join(", ")
          : "—";
        out.innerHTML = `<table class="table"><thead><tr><th>Feature</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table>
          <p class="muted" style="margin:8px 0 0;font-size:12px">Known features: ${cat}</p>`;
      }
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      const raw = String((e && e.message) || e || "");
      // Rolling API restarts / flag off → FastAPI 404. Soft-fail; do not toast spam.
      if (/\b404\b/.test(raw) || /\b503\b/.test(raw) || /not\s*found/i.test(raw) || /unavailable/i.test(raw)) {
        if (status) {
          status.textContent =
            "Feature registry is temporarily unavailable — refresh after the server finishes updating.";
        }
        if (out) {
          out.innerHTML =
            `<p class="muted">Device feature registry did not answer. Pins and commands still work.</p>`;
        }
        return;
      }
      if (status) status.textContent = raw;
      toast(raw, "err");
    }
  };

  void refresh();

  if (!canEdit) return;
  const btn = view.querySelector("#deviceCapsRegister");
  if (!btn) return;
  btn.onclick = async () => {
    const deviceType = String((view.querySelector("#deviceCapsType") && view.querySelector("#deviceCapsType").value) || "camera_generic").trim();
    const claimed = String((view.querySelector("#deviceCapsClaimed") && view.querySelector("#deviceCapsClaimed").value) || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const tested = String((view.querySelector("#deviceCapsTested") && view.querySelector("#deviceCapsTested").value) || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!claimed.length) {
      if (status) status.textContent = "At least one claimed capability slug is required.";
      return;
    }
    if (!window.confirm(`Register capabilities [${claimed.join(", ")}] on ${deviceId}?`)) return;
    btn.disabled = true;
    try {
      const _pack = await api(buildPostDeviceCapabilitiesPath(deviceId), {
        method: "POST",
        body: {
          device_type: deviceType,
          claimed,
          tested: tested.length ? tested : undefined,
        },
        timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
      });
      if (!isRouteCurrent(routeSeq)) return;
      if (out) {
        out.innerHTML = `<p class="muted" style="margin:0">Registered. Refreshing…</p>`;
      }
      toast("Capabilities registered", "ok");
      await refresh();
    } catch (e) {
      if (status) status.textContent = String(e.message || e);
      toast(String(e.message || e), "err");
    } finally {
      btn.disabled = false;
    }
  };
}

/**
 * Mount fleet capability index into view (bare #/devices).
 * @param {HTMLElement} view
 * @param {{
 *   routeSeq: number,
 *   isRouteCurrent: (n: number) => boolean,
 *   api: Function,
 *   toast: Function,
 *   escapeHtml: Function,
 *   mountView: Function,
 * }} ctx
 */
export async function mountDeviceCapabilitiesFleet(view, ctx) {
  const { routeSeq, isRouteCurrent, api, toast, escapeHtml, mountView } = ctx;

  const render = async (filter) => {
    mountView(view, `<div class="card"><p class="muted">Loading capability registry…</p></div>`);
    try {
      const query = { verified_only: "false" };
      if (filter) query.capability = filter;
      const pack = await api(buildGetDevicesCapabilitiesPath(query), {
        timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
      });
      if (!isRouteCurrent(routeSeq)) return;
      const catalog = Array.isArray(pack.catalog) ? pack.catalog : [];
      const items = Array.isArray(pack.items) ? pack.items : [];
      const catalogHtml = escapeHtml(catalog.join(", ") || "—");
      const itemsHtml = items.map((it) => {
        const did = String(it.device_id || it.id || "");
        const caps = Array.isArray(it.capabilities)
          ? it.capabilities.join(", ")
          : Array.isArray(it.claimed)
            ? it.claimed.join(", ")
            : String(it.capabilities || it.claimed || "—");
        return `<tr>
          <td class="mono">${escapeHtml(did || "—")}</td>
          <td class="mono">${escapeHtml(String(it.device_type || it.brand || "—"))}</td>
          <td class="mono">${escapeHtml(caps)}</td>
          <td>${did ? `<a class="btn sm secondary btn-tap" href="#/devices/${encodeURIComponent(did)}">Open</a>` : ""}</td>
        </tr>`;
      }).join("");
      mountView(view, deviceCapabilitiesFleetHtml({
        catalogHtml,
        itemsHtml,
        filter: filter || "",
        escapeHtml,
      }));
      const refresh = view.querySelector("#capsFleetRefresh");
      if (refresh) {
        refresh.onclick = () => {
          const f = String((view.querySelector("#capsFleetFilter") && view.querySelector("#capsFleetFilter").value) || "").trim();
          void render(f);
        };
      }
      const filterEl = view.querySelector("#capsFleetFilter");
      if (filterEl) {
        filterEl.addEventListener("keydown", (ev) => {
          if (ev.key === "Enter") {
            void render(String(filterEl.value || "").trim());
          }
        });
      }
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      mountView(view, `<div class="card glass-panel glass-panel--panel">
        <h2>Registered board features</h2>
        <p class="muted">${escapeHtml(String(e.message || e))}</p>
        <a class="btn secondary btn-tap" href="#/group/_all">All boards</a>
      </div>`);
      toast(String(e.message || e), "err");
    }
  };

  await render("");
}
